import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How to wait for DOM elements to show up in modern browsers",
  "description": "",
  "published": "2015-03-26T00:00:00.000Z",
  "redirect_from": ["/blog/how-to-properly-wait-for-dom-elements-to-show-up-in-modern-browsers/swizec/6663"],
  "categories": "Document Object Model, DOM events, JavaScript, jQuery, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Remember how careful we all were of the DOM ready event a few years ago? Writing JavaScript code outside of a `}<inlineCode parentName="p">{`$(document).ready()`}</inlineCode>{` callback was sacrilege. An offense of the highest order.`}</p>
    <p>{`Then about two years ago somebody went `}<em parentName="p">{`"Whoa whoa, wait a minute. We don't need all of that DOM ready nonsense. We can just put our JavaScript at the bottom"`}</em></p>
    <p>{`And everyone was like, `}<em parentName="p">{`"Haha silly us, thinking that script tags belong in the header. They can go anywhere!"`}</em></p>
    <p>{`Nowadays, no JavaScript ever goes in a `}<inlineCode parentName="p">{`$(document).ready()`}</inlineCode>{` callback. Our scripts' hair flows in the wind as they ride through the glen firing arrows into the sunset. Khem.`}</p>
    <p>{`Anyway, the point is. We've all but forgotten about the DOM ready event. By the time our scripts run, the whole DOM is already there and we don't have to worry about accessing elements that don't exist.`}</p>
    <p>{`That is, until we start rendering our elements with JavaScript.`}</p>
    <h2 {...{
      "id": "building-dom-with-js-can-mess-things-up"
    }}>{`Building DOM with JS can mess things up`}</h2>
    <p>{`In an ideal world, you'd wholly rely on your fancy framework to build the DOM. Backbone, Angular, CanJS, React, anything really. They're all smart enough to keep you from doing stupid things.`}</p>
    <p>{`But sometimes you're still going to do something stupid. Like, you'll have some init code that needs an element for some reason or another. In my case, I needed to set its width based on some stuff in local storage.`}</p>
    <p>{`However, for whatever reason deep inside your legacy code, your init function gets called before the element you need is rendered. This can happen because you're doing ugly things with your framework, like, `}<inlineCode parentName="p">{`setTimeout(foo, 100); // this solves a render loop conflict`}</inlineCode>{`. Or as often happens, the element depends on a different component, and your framework decides on the wrong order of rendering.`}</p>
    <p>{`Frameworks are fickle like that. They really really like to pretend everything renders at the same time, but in reality it renders in a loop.`}</p>
    <p>{`And then you're tempted to write something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`   function try () {
     if (!$("#element").size()) {
       setTimeout(try, 500); // give everything some time to render
     }
   }
`}</code></pre>
    <p>{`And it works. It really does. As long as you keep looking at it.`}</p>
    <p>{`But modern browsers are tricky. When the tab isn't in focus, or your user's computer is running slow, or whatever, that timeout won't have enough time.`}</p>
    <p>{`You see, browsers these days are trying desperately to conserve batteries and generally be nice to computers. When a tab isn't in focus, the JavaScript slows down. Timeouts start acting funny, and you should forget all about any rendering happening.`}</p>
    <p>{`You'll notice this easiest when switching to your app's tab. It shows the old content. Then flashes. Then re-renders.`}</p>
    <p>{`Really really really annoying for testing because you can no longer browse HackerNews while your app takes its 20 seconds to reload. But really useful for users.`}</p>
    <p>{`Please don't ask why the app I'm working on takes 20 seconds to reload.`}</p>
    <p>{`But anyway, you've hit upon the problem of DOM elements not being there when your code needs them. No matter how long the delay in that setTimeout, the element still isn't there.`}</p>
    <p>{`"A-ha! DOM ready! I need the DOM ready!", you think.`}</p>
    <p>{`Nope. The DOM is long since ready by the your script runs. I've tried that.`}</p>
    <p>{`Turns out what you're looking for is `}<inlineCode parentName="p">{`requestAnimationFrame`}</inlineCode>{`. It's normally used to make animation less jittery because it syncs your JavaScript's rendering with the browser's rendering. Normally about 60 hertz.`}</p>
    <p>{`But it's also how the browser tells your code that it's still waiting. That things haven't been rendered because the user isn't there and there's nobody to look at your stuff.`}</p>
    <p>{`So instead of that setTimeout, you should do something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`  function try() {
    if (!$("#element").size()) {
      window.requestAnimationFrame(try);
    }else {
       $("#element").do_some_stuff();
     }
  };
`}</code></pre>
    <p>{`Yup, no timeouts. At first glance it looks like this code is going to poll the DOM every 60th of a second to see if the element is there yet.`}</p>
    <p>{`But in practice it only ever retries once. Because no matter what, by the next render frame, whether it comes in a 60th of a second, or a minute, the element will have been rendered.`}</p>
    <p>{`And that's how you properly wait for DOM elements to show up in modern browsers. I nearly tore my hair out before I figured it out.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      